import { BufferGeometry, type Color, Float32BufferAttribute, Group, Mesh, MeshBasicMaterial } from 'three';
import type { RacingLine } from '../track/racingLine';
import type { TrackData } from '../track/types';
import { PAL } from './palette';
import { asphaltStops, type RoadPoint, roadPoint } from './trackMesh';

/**
 * The driving lesson's paint on the road: a red braking ribbon for each
 * braking event, from where a beginner starts braking to the slowest point,
 * with a cream "brake here" stripe across the road where it starts; and a
 * cream racing line over the stretch just ahead of the car. Render only: it
 * reads the track and never touches the simulation.
 *
 * The paint is cut from the asphalt's own triangles and lifted a few
 * centimetres, so it follows the elevation, the banking and every twist of the
 * road exactly: it never sinks into a banked bend or floats over a crest. It is
 * depth tested, so the car, the hills and a crossover's bridge all hide it.
 */
export interface LessonOverlayEvent {
  /** Sample where the beginner starts braking, and where the speed bottoms out. */
  brakeIndex: number;
  minIndex: number;
  /** Side the corner turns to (+1 left). Not drawn: the racing line already shows the way. */
  dir: number;
}

export interface LessonOverlay {
  readonly group: Group;
  /** Which braking ribbons to show: 'none', 'all', or one event index. */
  showEvents(which: 'none' | 'all' | number): void;
  /** The racing line ribbon ahead of the car. */
  setLine(on: boolean): void;
  /** Per frame: pos = fractional sample index of the car; t = seconds (for a gentle pulse). */
  update(pos: number, t: number): void;
}

/** How the lesson's paint looks. */
export const LESSON_PAINT = {
  /** Heights above the road's base: asphalt is at 0.03, the edge lines 0.035, curbs up to 0.11. */
  brakeY: 0.052,
  lineY: 0.058,
  /** The braking ribbon stops this far inside the road's edges (the white lines are the outer 0.28 m). */
  brakeInset: 0.4,
  /**
   * The "brake here" stripe: its length, and the strip of bare asphalt between it and the red
   * (metres). It ends just before the braking point, so the red starts exactly there.
   */
  stripe: 2.6,
  stripeGap: 0.5,
  /** Opacity of the red at the start of the ribbon and at its end, before the last stretch fades out. */
  brakeAlpha: [0.62, 0.3] as const,
  /** The last part of the ribbon (fraction of it) fades to nothing at the slowest point. */
  brakeTail: 0.14,
  stripeAlpha: 0.95,
  /** The pulse: how much the ribbons dim at its low point, and its rate (Hz). */
  pulse: 0.14,
  pulseHz: 0.8,
  /** Half width of the racing line, metres, and its opacity. */
  lineHalf: 0.45,
  lineAlpha: 0.82,
  /** How far ahead of the car the line shows (metres), and the share of that over which it fades out. */
  lineAhead: 220,
  lineFade: 0.45,
  /** Samples of line kept behind the car, fading in underneath it. */
  lineBehind: 3,
};

/**
 * Draw order among the see-through things: the ribbons, then the line over them, both before
 * everything else (a fading ghost car is at 0), since paint on the road is never in front of a car.
 */
const PAINT_ORDER = -2;

type UV = [number, number];
type Paint = (u: number) => readonly [number, number, number, number];

/** Keeps the part of a convex polygon where a*u + b*v + c >= 0 (one Sutherland-Hodgman pass). */
function clip(poly: UV[], a: number, b: number, c: number): UV[] {
  const out: UV[] = [];
  for (let k = 0; k < poly.length; k++) {
    const p = poly[k];
    const q = poly[(k + 1) % poly.length];
    const dp = a * p[0] + b * p[1] + c;
    const dq = a * q[0] + b * q[1] + c;
    if (dp >= 0) out.push(p);
    if (dp >= 0 !== dq >= 0) {
      const f = dp / (dp - dq);
      const r: UV = [p[0] + (q[0] - p[0]) * f, p[1] + (q[1] - p[1]) * f];
      const last = out[out.length - 1];
      if (!last || Math.abs(last[0] - r[0]) > 1e-9 || Math.abs(last[1] - r[1]) > 1e-9) out.push(r);
    }
  }
  return out;
}

/** Twice the signed area of a polygon in (u, v). */
function area2(poly: UV[]): number {
  let a = 0;
  for (let k = 0; k < poly.length; k++) {
    const p = poly[k];
    const q = poly[(k + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a;
}

/**
 * An indexed triangle list with RGBA vertex colours and a position along the
 * track per vertex. Points shared by neighbouring pieces (same place, colour
 * and position along) are stored once.
 */
class Paintwork {
  pos: number[] = [];
  col: number[] = [];
  along: number[] = [];
  index: number[] = [];
  private seen = new Map<string, number>();

  constructor(
    private t: TrackData,
    private P: RoadPoint,
    private lineOffset: number[],
  ) {}

  /** Indices so far: where the next piece starts in the index buffer. */
  get count(): number {
    return this.index.length;
  }

  /**
   * Paint between samples i and i + 1: the stretch of road from lateral offset
   * lo to hi (lo0..hi0 at i, lo1..hi1 at i + 1), from fraction u0 to u1 of the
   * way along, `y` above the road's base. Each asphalt band's quad is mapped to
   * the unit square (u along, v across) and split on its diagonal exactly like
   * the road mesh; the paint's outline is clipped against both triangles and
   * mapped through each one's own plane, so the paint is parallel to the road
   * wherever a banked or climbing bend twists its quads.
   */
  drape(i: number, lo0: number, hi0: number, lo1: number, hi1: number, u0: number, u1: number, y: number, paint: Paint, along = 0): void {
    const n = this.t.n;
    const hw = this.t.halfWidth;
    const S0 = asphaltStops(hw, this.lineOffset[((i % n) + n) % n]);
    const S1 = asphaltStops(hw, this.lineOffset[(((i + 1) % n) + n) % n]);
    for (let k = 0; k < S0.length - 1; k++) {
      const p0 = S0[k];
      const w0 = S0[k + 1] - p0;
      const p1 = S1[k];
      const w1 = S1[k + 1] - p1;
      if (w0 <= 0 || w1 <= 0) continue;
      let poly: UV[] = [
        [0, (lo0 - p0) / w0],
        [1, (lo1 - p1) / w1],
        [1, (hi1 - p1) / w1],
        [0, (hi0 - p0) / w0],
      ];
      poly = clip(poly, 0, 1, 0);
      poly = clip(poly, 0, -1, 1);
      poly = clip(poly, 1, 0, -u0);
      poly = clip(poly, -1, 0, u1);
      if (poly.length < 3 || area2(poly) < 1e-9) continue;
      const A = this.P(i, p0, y);
      const B = this.P(i + 1, p1, y);
      const C = this.P(i + 1, p1 + w1, y);
      const D = this.P(i, p0 + w0, y);
      // Triangle ABC holds u >= v, triangle ACD holds u <= v (the road's own diagonal, A to C).
      this.fan(clip(poly, 1, -1, 0), (u, v) => [0, 1, 2].map((c) => A[c] + u * (B[c] - A[c]) + v * (C[c] - B[c])), paint, along);
      this.fan(clip(poly, -1, 1, 0), (u, v) => [0, 1, 2].map((c) => A[c] + v * (D[c] - A[c]) + u * (C[c] - D[c])), paint, along);
    }
  }

  private fan(poly: UV[], map: (u: number, v: number) => number[], paint: Paint, along: number): void {
    if (poly.length < 3 || area2(poly) < 1e-9) return;
    const ids = poly.map((p) => this.vertex(map(p[0], p[1]), paint(p[0]), along + p[0]));
    for (let k = 1; k + 1 < ids.length; k++) this.index.push(ids[0], ids[k], ids[k + 1]);
  }

  private vertex(x: number[], c: readonly number[], along: number): number {
    const q = (v: number, s: number): number => Math.round(v * s);
    const key = `${q(x[0], 1e5)},${q(x[1], 1e5)},${q(x[2], 1e5)},${q(c[0], 1e4)},${q(c[3], 1e4)},${q(along, 1e4)}`;
    let id = this.seen.get(key);
    if (id === undefined) {
      id = this.pos.length / 3;
      this.seen.set(key, id);
      this.pos.push(x[0], x[1], x[2]);
      this.col.push(c[0], c[1], c[2], c[3]);
      this.along.push(along);
    }
    return id;
  }

  mesh(material: MeshBasicMaterial, withAlong: boolean): Mesh {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new Float32BufferAttribute(this.col, 4));
    if (withAlong) g.setAttribute('along', new Float32BufferAttribute(this.along, 1));
    g.setIndex(this.index);
    this.seen.clear();
    const m = new Mesh(g, material);
    m.renderOrder = PAINT_ORDER;
    m.visible = false;
    return m;
  }
}

/** Unlit, see-through paint that the road, car and bridge still hide, drawn over the asphalt without fighting it. */
function paintMaterial(): MeshBasicMaterial {
  return new MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
}

function rgba(c: Color, a: number): readonly [number, number, number, number] {
  return [c.r, c.g, c.b, a];
}

export function buildLessonOverlay(track: TrackData, line: RacingLine, events: LessonOverlayEvent[]): LessonOverlay {
  const t = track;
  const n = t.n;
  const L = t.length;
  const hw = t.halfWidth;
  const P = roadPoint(t);
  const cfg = LESSON_PAINT;
  const group = new Group();
  group.name = 'lesson';
  /** Metres from sample i to the next one (the last wraps to the line). */
  const segLen = (i: number): number => {
    const k = ((i % n) + n) % n;
    return (((t.s[(k + 1) % n] - t.s[k]) % L) + L) % L;
  };

  /** Paints from metre dFrom to dTo past sample b (negative: before it), lo to hi across, coloured by the distance from b. */
  const stretch = (work: Paintwork, b: number, dFrom: number, dTo: number, lo: number, hi: number, y: number, colour: (d: number) => ReturnType<Paint>): void => {
    let i = b;
    let at = 0;
    while (at > dFrom) at -= segLen(--i);
    for (; at < dTo; at += segLen(i++)) {
      const len = segLen(i);
      const u0 = Math.max(0, (dFrom - at) / len);
      const u1 = Math.min(1, (dTo - at) / len);
      const from = at;
      if (len > 0 && u1 > u0) work.drape(i, lo, hi, lo, hi, u0, u1, y, (u) => colour(from + u * len));
    }
  };

  // --- braking ribbons: one mesh each, red fading from strong to nothing at the slowest point.
  const ribbons: Mesh[] = [];
  const ribbonMats: MeshBasicMaterial[] = [];
  const w = hw - cfg.brakeInset;
  const cream = rgba(PAL.cream, cfg.stripeAlpha);
  for (const ev of events) {
    const b = ((ev.brakeIndex % n) + n) % n;
    const steps = (((ev.minIndex - b) % n) + n) % n;
    let total = 0;
    for (let q = 0; q < steps; q++) total += segLen(b + q);
    const [a0, a1] = cfg.brakeAlpha;
    const red = (d: number): ReturnType<Paint> => {
      const f = total > 0 ? Math.min(1, Math.max(0, d / total)) : 1;
      const tail = Math.min(1, (1 - f) / cfg.brakeTail);
      return rgba(PAL.red, (a0 + (a1 - a0) * f) * tail);
    };
    const paint = new Paintwork(t, P, line.offset);
    stretch(paint, b, -cfg.stripeGap - cfg.stripe, -cfg.stripeGap, -w, w, cfg.brakeY, () => cream);
    stretch(paint, b, 0, total, -w, w, cfg.brakeY, red);
    const mat = paintMaterial();
    const mesh = paint.mesh(mat, false);
    mesh.name = `lesson-brake-${ribbons.length}`;
    group.add(mesh);
    ribbons.push(mesh);
    ribbonMats.push(mat);
  }

  // --- racing line: the whole lap plus the first stretch again, so the window ahead never wraps.
  const ds = L / n;
  const ahead = Math.max(8, Math.ceil(cfg.lineAhead / ds));
  const behind = cfg.lineBehind;
  const span = ahead + behind;
  const lineWork = new Paintwork(t, P, line.offset);
  /** Where each step's triangles start in the index buffer. */
  const firstIndex = new Int32Array(n + span + 1);
  const lineColour = rgba(PAL.cream, cfg.lineAlpha);
  const half = cfg.lineHalf;
  for (let q = 0; q < n + span; q++) {
    firstIndex[q] = lineWork.count;
    const o0 = line.offset[q % n];
    const o1 = line.offset[(q + 1) % n];
    lineWork.drape(q, o0 - half, o0 + half, o1 - half, o1 + half, 0, 1, cfg.lineY, () => lineColour, q);
  }
  firstIndex[n + span] = lineWork.count;
  const lineMat = paintMaterial();
  // Faded by the distance along the track from the car (in samples), on the GPU: moving the window costs no uploads.
  const uniforms = { uPos: { value: behind }, uAhead: { value: ahead }, uFade: { value: ahead * cfg.lineFade } };
  lineMat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float along;\nvarying float vAlong;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAlong = along;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uPos;\nuniform float uAhead;\nuniform float uFade;\nvarying float vAlong;')
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\nfloat dAhead = vAlong - uPos;\ndiffuseColor.a *= smoothstep(-2.0, 0.0, dAhead) * (1.0 - smoothstep(uAhead - uFade, uAhead, dAhead));',
      );
  };
  lineMat.customProgramCacheKey = () => 'lesson-line';
  const lineMesh = lineWork.mesh(lineMat, true);
  lineMesh.name = 'lesson-line';
  lineMesh.renderOrder = PAINT_ORDER + 1;
  lineMesh.geometry.setDrawRange(0, firstIndex[span]);
  group.add(lineMesh);

  let shown: 'none' | 'all' | number = 'none';
  return {
    group,
    showEvents(which) {
      shown = which;
      ribbons.forEach((m, k) => (m.visible = which === 'all' || which === k));
    },
    setLine(on) {
      lineMesh.visible = on;
    },
    update(pos, time) {
      // The pulse: a slow breath on whichever ribbons are showing.
      if (shown !== 'none') {
        const o = 1 - cfg.pulse * (0.5 - 0.5 * Math.cos(time * Math.PI * 2 * cfg.pulseHz));
        for (const m of ribbonMats) m.opacity = o;
      }
      if (!lineMesh.visible || !Number.isFinite(pos)) return;
      const p = ((pos % n) + n) % n;
      const whole = Math.floor(p);
      let start = whole - behind;
      if (start < 0) start += n;
      uniforms.uPos.value = start + behind + (p - whole);
      lineMesh.geometry.setDrawRange(firstIndex[start], firstIndex[start + span] - firstIndex[start]);
    },
  };
}
